/**
 * Gomme magique : what happens between "Gommer" in a photo's tools and the photo replaced, shared
 * by the mobile EditPhoto (its erase mode) and the desktop shared.popups.MagicEraserPopup. The
 * screen owns the layout and wires its buttons to run() / clear() / cancel() / retry() / keep() ;
 * this owns the steps, the brush, the loader, the waiting and the server.
 *
 *   drawing      the customer circles what must go          Tout effacer · Gommer la zone (off until a stroke)
 *   processing   the erase runs, the area shimmering         every button off
 *   waiting      still running after CANCEL_DELAY            Annuler
 *   comparing    the result shown alone over the photo       Recommencer · Garder
 *   keeping      the photo being replaced on the server      every button off
 *
 *   let flow = new shared.magiceraser.EraseFlow({ view, hint, buttons : { back, clear, run, cancel,
 *                                                  retry, keep }, component, slot, model, onLayout, onKept });
 *   flow.start(media);   …   flow.stop();
 *
 * `component` is the CreationElementContent whose photo is erased, `slot` the album's place it stands
 * for when it is a copy (mobile EditPhoto) ; keep() gives that place a new photo holding the erased
 * file (MagicEraserService.keep), the other places keeping the original, and calls onKept. onLayout is called whenever the
 * buttons on show change, for the screen to lay its bar out (see layoutBar).
 *
 * The hint under the photo follows the steps (TEXTS) and "Gommer la zone" reads "Gommage…" while
 * the erase runs.
 */
fx.ns("shared.magiceraser", class EraseFlow {

    constructor({ view, hint, buttons, component, slot = null, model, onLayout = () => {}, onKept = () => {} })
    {
        Object.assign(this, { view, hint, buttons, component, slot, model, onLayout, onKept });

        this.service = fx.service("MagicEraserService");
        this.brush = null;
        this.step = "drawing";

        this._edit = null;
        this._job = null;
        this._timer = null;
        this._stopped = false;
    }

    // the thinnest brush : enough to circle something, and circling fills it
    get BRUSH_SIZE()
    {
        return 10;
    }

    // waited this long, the customer is offered to give up
    get CANCEL_DELAY()
    {
        return 10000;
    }

    get busy()
    {
        return this.step == "processing" || this.step == "waiting" || this.step == "keeping";
    }

    // the hint of each step : nothing drawn yet, an area drawn, the erase running, the result shown
    get TEXTS()
    {
        return {
            empty : fx.text("Entourez la zone à gommer : tracez un contour fermé autour de l'élément"),
            drawn : fx.text("L'intérieur du contour sera gommé. Vous pouvez entourer une autre zone."),
            processing : fx.text("Ne fermez pas la fenêtre, c'est presque fini."),
            result : fx.text("Voici le résultat. Gardez-le, ou recommencez"),
        };
    }

    get RUN_LABELS()
    {
        return { run : fx.text("Gommer la zone"), running : fx.text("Gommage…") };
    }

    // ---- lifecycle

    start(media)
    {
        this._media = media;
        this._setStep("drawing");
        this.view.busy(true);

        this.view.load(media.getUrl(this.version))
            .then((image) => {
                this.view.busy(false);

                if (this._stopped)
                    return;

                this.brush = new shared.magiceraser.Brush({ image : image, brushSize : this.BRUSH_SIZE, onStatus : () => this._updateButtons() });
                this._updateButtons();
            })
            .catch(() => {
                this.view.busy(false);
                this._failed("failed");
            });
    }

    // leaving : a running erase is dropped, the brush lets go of the window, a result not kept is
    // discarded
    stop()
    {
        this._stopped = true;

        if (this._edit && this.step == "comparing")
            this._discard();

        if (this._job)
            this._job.cancel();

        this._job = null;
        clearTimeout(this._timer);
        this.view.busy(false);
        this.view.stopErasing();
        this.view.hideResult();

        if (this.brush)
            this.brush.destroy();

        this.brush = null;
    }

    // the version of the photo on the page, '' being the photo itself
    get version()
    {
        return this.component.content_type || "";
    }

    // ---- actions

    clear()
    {
        if (this.brush && this.step == "drawing")
            this.brush.clear();

        this._updateButtons();
    }

    run()
    {
        if (!this.brush || this.brush.isEmpty || this.step != "drawing")
            return;

        this._setStep("processing");
        clearTimeout(this._timer);
        this._timer = setTimeout(() => {
            if (this.step == "processing")
                this._setStep("waiting");
        }, this.CANCEL_DELAY);

        let job = null;
        let bounds = this.brush.bounds;

        // the area shimmers where it was drawn, the strokes themselves hidden meanwhile
        this.view.erasing(this.brush.overlayDataURL(), bounds, this.brush.samplePoints(80));
        this.brush.enabled = false;

        this.brush.buildMaskDataURL()
            .then((mask) => {
                job = this._job = this.service.erase(this._media, this.version, mask);
                return job;
            })
            .then((edit) => {
                this._edit = edit;
                // the result grows from the middle of the area
                let reveal = bounds ? { x : (bounds.x0 + bounds.x1) / 2, y : (bounds.y0 + bounds.y1) / 2 } : null;

                return job == this._job ? this.view.showResult(edit.medium, { compare : false, reveal : reveal }) : null;
            })
            .then(() => {
                if (this._job != job)
                    return;

                this._job = null;
                clearTimeout(this._timer);
                this._setStep("comparing");
            })
            .catch((error) => {
                // cancelled : cancel() already put the screen back
                if (this._job != job || (error && error.code == "cancelled"))
                    return;

                this._job = null;
                clearTimeout(this._timer);
                this.view.stopErasing();
                this._setStep("drawing");
                this._failed(error && error.code ? error.code : "failed");
            });
    }

    // the customer gives up waiting : the request is dropped, the strokes stay to try again
    cancel()
    {
        if (this._job)
            this._job.cancel();

        this._job = null;
        clearTimeout(this._timer);
        this.view.stopErasing();
        this._setStep("drawing");
    }

    // another try : strokes gone, the photo as it was. The erase just made is discarded.
    retry()
    {
        this._discard();
        this._edit = null;
        this.view.hideResult();

        if (this.brush)
            this.brush.clear();

        this._setStep("drawing");
    }

    // the result not kept : its photo stays out of the album (already, until kept) and the erase
    // can no longer be kept. Nothing waits for the answer.
    _discard()
    {
        let edit = this._edit;
        this._edit = null;

        if (edit)
            this.service.discard(edit).catch(() => {});
    }

    // final : the erased place shows a new photo holding the erased file
    keep()
    {
        if (!this._edit || this.step != "comparing")
            return;

        this._setStep("keeping");

        this.service.keep(this._edit, this.component, this.slot, this.model.photos)
            .then(() => this.onKept())
            .catch((error) => {
                this._setStep("comparing");
                this._failed(error && error.code ? error.code : "failed");
            });
    }

    // ---- display

    /**
     * Lays the buttons on show side by side, centred in a bar `width` wide : a hidden one leaves
     * no gap. Each is at most maxButtonWidth wide — or widthOf(button), a primary one being wider
     * on desktop —, sharing what the bar has between its margins otherwise.
     */
    layoutBar(width, top, height, maxButtonWidth, { gap = 16, margin = gap, widthOf = null } = {})
    {
        let b = this.buttons;
        let shown = [b.clear, b.run, b.cancel, b.retry, b.keep].filter((button) => button && button.visible);

        if (!shown.length)
            return;

        let share = (width - 2 * margin - gap * (shown.length - 1)) / shown.length;
        let widths = shown.map((button) => Math.floor(Math.min(widthOf ? widthOf(button) : maxButtonWidth, share)));
        let left = (width - widths.reduce((sum, w) => sum + w, 0) - gap * (shown.length - 1)) / 2;

        shown.forEach((button, i) => {
            button.viewport({ width : widths[i], height : height, top : top, left : Math.round(left) });
            left += widths[i] + gap;
        });
    }

    // in pink when it reports a failure
    setHint(text, failed)
    {
        this.hint.toggleClass("failed", !!failed);
        this.hint.text = text ? '<span class="label">' + text + '</span>' : "";
    }

    _setStep(step)
    {
        this.step = step;

        let b = this.buttons;
        let drawing = step == "drawing" || step == "processing";

        b.clear.visible = drawing;
        b.run.visible = drawing;
        b.cancel.visible = step == "waiting";
        b.retry.visible = step == "comparing" || step == "keeping";
        b.keep.visible = step == "comparing" || step == "keeping";

        let runLabel = this.RUN_LABELS[step == "processing" ? "running" : "run"];
        if (b.run.label != runLabel)
            b.run.label = runLabel;

        this.onLayout();

        // the erase running has its own animation (erasing), the spinner is for the photo replaced
        this.view.busy(step == "keeping");

        if (this.brush)
        {
            this.brush.enabled = step == "drawing";
            this.brush.locked = step != "drawing";
        }

        let texts = this.TEXTS;

        if (step == "comparing")
            this.setHint(texts.result);
        else
        if (step == "processing")
            this.setHint(texts.processing);
        else
        if (step == "drawing")
            this.setHint(this.brush && !this.brush.isEmpty ? texts.drawn : texts.empty);

        this._updateButtons();
    }

    _updateButtons()
    {
        let b = this.buttons;
        let empty = !this.brush || this.brush.isEmpty;

        b.clear.disabled = this.busy || empty;
        b.run.disabled = this.busy || empty;
        b.retry.disabled = this.busy;
        b.keep.disabled = this.busy;

        if (b.back)
            b.back.disabled = this.busy;

        if (this.step == "drawing")
            this.setHint(empty ? this.TEXTS.empty : this.TEXTS.drawn);
    }

    _failed(code)
    {
        this.setHint(this.service.message(code), true);
    }
});
